import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, DownloadCloud, CheckCircle2, AlertCircle, Loader2, Users, Ticket, Check, Square, MapPin } from "lucide-react";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

const STAGES = [
  { key: "requested", label: "Requested" },
  { key: "in_progress", label: "In progress" },
  { key: "handover", label: "Handover" },
  { key: "porting", label: "Porting" },
  { key: "installed", label: "Installed" },
  { key: "live", label: "Live" },
  { key: "cancelled", label: "Cancelled" },
];

/**
 * Cherry-pick Syncro customers to import. Admin picks a partner → sees
 * every matching Syncro customer → ticks the small subset that belong to
 * us → sets each row's onboarding stage → commit. Idempotent.
 */
export default function SyncroImportModal({ onClose, onImported }) {
  const [partners, setPartners] = useState(null);
  const [partnerCode, setPartnerCode] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Per-row picks: {syncro_customer_id: {selected: bool, stage: str}}
  const [picks, setPicks] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/commissions/partners");
        setPartners(data.filter((p) => p.syncro_referrer_label));
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load partners");
      }
    })();
  }, []);

  async function loadPreview() {
    if (!partnerCode) return;
    setBusy(true); setPreview(null); setResult(null); setPicks({});
    try {
      const { data } = await api.get(`/commissions/partners/${partnerCode}/syncro-import-preview`);
      setPreview(data);
      // Default state: nothing selected; stage=live per row.
      const init = {};
      (data.candidates || []).forEach((c) => {
        init[c.syncro_customer_id] = {
          selected: false,
          stage: "installed",  // default per user preference — hides on the Kanban "Installed" column
          service_address: c.service_address || "",
        };
      });
      setPicks(init);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    } finally { setBusy(false); }
  }

  const selectedCount = useMemo(
    () => Object.values(picks).filter((p) => p.selected).length,
    [picks]
  );

  function toggleAll(next) {
    setPicks((prev) => {
      const out = { ...prev };
      Object.keys(out).forEach((k) => { out[k] = { ...out[k], selected: next }; });
      return out;
    });
  }

  function setRow(sid, patch) {
    setPicks((prev) => ({ ...prev, [sid]: { ...prev[sid], ...patch } }));
  }

  async function commit() {
    if (!partnerCode || selectedCount === 0) {
      toast.error("Pick at least one customer to import"); return;
    }
    const selections = Object.entries(picks)
      .filter(([, v]) => v.selected)
      .map(([sid, v]) => ({
        syncro_customer_id: sid,
        stage: v.stage || "installed",
        service_address: (v.service_address || "").trim() || undefined,
        service_address_parts: v.service_address_parts || undefined,
      }));
    setBusy(true);
    try {
      const { data } = await api.post(
        `/commissions/partners/${partnerCode}/syncro-import`,
        { selections },
      );
      setResult(data);
      toast.success(`Imported ${data.imported} new, updated ${data.updated}, linked ${data.linked_tickets} tickets`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  }

  const allSelected = preview && preview.candidate_count > 0 && selectedCount === preview.candidate_count;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="syncro-import-modal">
      <button type="button" aria-label="Close" onClick={onClose}
              className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-3xl max-h-[85vh] bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <header className="flex items-start justify-between gap-4 p-6 border-b border-slate-800">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold mb-1">Cherry-pick import</div>
            <h2 className="font-display text-2xl tracking-tight text-slate-50">Import Syncro customers</h2>
            <p className="text-sm text-slate-400 mt-1 max-w-xl">
              Tick the customers who are on NBN with you, set each one&apos;s onboarding stage, and we&apos;ll pull them in with their linked install ticket.
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="syncro-import-close"
                  className="p-2 rounded-md text-slate-400 hover:text-slate-50 hover:bg-slate-800 shrink-0">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Step 1 — partner */}
          <section>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Step 1 · Partner</div>
            {partners === null ? (
              <div className="text-slate-500 text-sm">Loading partners…</div>
            ) : partners.length === 0 ? (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-200 text-sm p-4">
                No partners have a <code>syncro_referrer_label</code>. Set one on the partner profile first.
              </div>
            ) : (
              <div className="flex gap-2 items-center">
                <select
                  data-testid="syncro-import-partner-select"
                  value={partnerCode}
                  onChange={(e) => { setPartnerCode(e.target.value); setPreview(null); setResult(null); }}
                  className="flex-1 rounded-lg border border-slate-800 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-cyan-500"
                >
                  <option value="">— pick a partner —</option>
                  {partners.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.name || p.code} (referrer: {p.syncro_referrer_label})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={loadPreview}
                  disabled={!partnerCode || busy}
                  data-testid="syncro-import-preview-btn"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-semibold disabled:opacity-50"
                >
                  {busy && !preview ? <Loader2 className="w-4 h-4 animate-spin" /> : <DownloadCloud className="w-4 h-4" />}
                  Find candidates
                </button>
              </div>
            )}
          </section>

          {/* Step 2 — pick */}
          {preview && !result && (
            <section data-testid="syncro-import-preview">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                  Step 2 · Pick customers &amp; stages
                </div>
                {preview.candidate_count > 0 && (
                  <button
                    type="button"
                    onClick={() => toggleAll(!allSelected)}
                    data-testid="syncro-import-toggle-all"
                    className="inline-flex items-center gap-1.5 text-xs text-cyan-300 hover:text-cyan-200 font-semibold"
                  >
                    {allSelected ? <Square className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                    {allSelected ? "Clear all" : "Select all"}
                  </button>
                )}
              </div>

              {preview.candidate_count === 0 ? (
                <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-6 text-center text-sm text-slate-400" data-testid="syncro-import-empty">
                  No Syncro customers found with <code>referred_by = {preview.syncro_referrer_label}</code>.
                </div>
              ) : (
                <>
                  <div className="rounded-lg border border-slate-800 bg-slate-950/40 divide-y divide-slate-800/60 max-h-80 overflow-y-auto">
                    {preview.candidates.map((c) => {
                      const p = picks[c.syncro_customer_id] || { selected: false, stage: "installed", service_address: "" };
                      return (
                        <div
                          key={c.syncro_customer_id}
                          data-testid={`syncro-row-${c.syncro_customer_id}`}
                          className={`px-4 py-2.5 text-sm ${p.selected ? "bg-cyan-500/5" : ""}`}
                        >
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={p.selected}
                              onChange={(e) => setRow(c.syncro_customer_id, { selected: e.target.checked })}
                              data-testid={`syncro-row-check-${c.syncro_customer_id}`}
                              className="w-4 h-4 rounded border-slate-700 bg-slate-950 accent-cyan-500 shrink-0"
                            />
                            <Users className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-slate-100 truncate">{c.business_name || c.contact_name || "(no name)"}</div>
                              <div className="text-xs text-slate-500 truncate">
                                {c.email || c.phone || "—"}
                              </div>
                            </div>
                            <select
                              value={p.stage || "installed"}
                              onChange={(e) => setRow(c.syncro_customer_id, { stage: e.target.value })}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`syncro-row-stage-${c.syncro_customer_id}`}
                              disabled={!p.selected}
                              className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-100 disabled:opacity-50"
                            >
                              {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                            </select>
                            {c.already_imported ? (
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-300 shrink-0 w-20 text-right">will update</span>
                            ) : (
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-cyan-300 shrink-0 w-20 text-right">new</span>
                            )}
                          </label>
                          {/* Address confirmation — only shown once the row is ticked */}
                          {p.selected && (
                            <div className="mt-2 pl-7 flex items-start gap-2" data-testid={`syncro-row-address-${c.syncro_customer_id}`}>
                              <MapPin className="w-3.5 h-3.5 text-slate-500 shrink-0 mt-2" />
                              <div className="flex-1 min-w-0">
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Service address (correct before landing on Kanban)</div>
                                <AddressAutocomplete
                                  value={p.service_address || ""}
                                  onChange={(v) => setRow(c.syncro_customer_id, { service_address: v })}
                                  onSelect={(parts) => setRow(c.syncro_customer_id, {
                                    service_address: parts?.formatted || p.service_address,
                                    service_address_parts: parts || null,
                                  })}
                                  placeholder="Start typing the NBN service address…"
                                  testid={`syncro-row-address-input-${c.syncro_customer_id}`}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-2">
                    <div className="text-xs text-slate-400">
                      <span data-testid="syncro-import-selected-count" className="font-semibold text-slate-100">{selectedCount}</span> of {preview.candidate_count} selected
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={onClose}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm">
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={commit}
                        disabled={busy || selectedCount === 0}
                        data-testid="syncro-import-commit-btn"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-semibold disabled:opacity-50"
                      >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <DownloadCloud className="w-4 h-4" />}
                        {busy ? "Importing…" : `Import ${selectedCount} selected`}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </section>
          )}

          {/* Step 3 — result */}
          {result && (
            <section data-testid="syncro-import-result">
              <div className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-2 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> Done
              </div>
              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4 flex items-center gap-4 flex-wrap">
                <Stat label="Imported" value={result.imported} tone="emerald" />
                <Stat label="Updated" value={result.updated} tone="amber" />
                <Stat label="Tickets linked" value={result.linked_tickets} tone="cyan" />
                {result.skipped > 0 && <Stat label="Skipped" value={result.skipped} />}
              </div>
              {result.errors?.length > 0 && (
                <div className="mt-3 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-xs text-rose-200 space-y-1">
                  <div className="flex items-center gap-1.5 font-semibold uppercase tracking-widest text-[10px]">
                    <AlertCircle className="w-3 h-3" /> {result.errors.length} error(s)
                  </div>
                  {result.errors.slice(0, 5).map((e, i) => (<div key={`${i}-${String(e).slice(0, 40)}`}>• {e}</div>))}
                </div>
              )}
              <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950/40 divide-y divide-slate-800/60 max-h-56 overflow-y-auto">
                {result.rows.map((row) => (
                  <div key={row.syncro_customer_id} className="flex items-center gap-3 px-4 py-2 text-sm">
                    <span className={`text-[10px] font-semibold uppercase tracking-wider shrink-0 ${row.action === "created" ? "text-cyan-300" : "text-amber-300"}`}>{row.action}</span>
                    <div className="flex-1 min-w-0 truncate text-slate-100">{row.business_name || row.syncro_customer_id}</div>
                    {row.stage && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 shrink-0">
                        {row.stage.replace(/_/g, " ")}
                      </span>
                    )}
                    {row.ticket_number && (
                      <span className="inline-flex items-center gap-1 text-cyan-300 text-xs font-mono shrink-0">
                        <Ticket className="w-3 h-3" /> #{row.ticket_number}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => onImported?.()}
                  data-testid="syncro-import-done"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-semibold"
                >
                  Close &amp; refresh
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "slate" }) {
  const toneClass = {
    slate: "text-slate-100", cyan: "text-cyan-300",
    amber: "text-amber-300", emerald: "text-emerald-300",
  }[tone] || "text-slate-100";
  return (
    <div className="min-w-[80px]">
      <div className={`font-display text-2xl tracking-tight ${toneClass}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
    </div>
  );
}
